import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LogOut, Mail, Phone, MessageSquare, Calendar, Building, Package, Image } from 'lucide-react';
import { toast } from 'sonner';

// Use current domain for API calls (works on all domains)
const BACKEND_URL = window.location.hostname === 'localhost' 
  ? process.env.REACT_APP_BACKEND_URL 
  : window.location.origin;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState('contacts');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      // Fetch contacts
      const contactsResponse = await fetch(`${BACKEND_URL}/api/contacts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Fetch quotes
      const quotesResponse = await fetch(`${BACKEND_URL}/api/quotes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (contactsResponse.ok && quotesResponse.ok) {
        const contactsData = await contactsResponse.json();
        const quotesData = await quotesResponse.json();
        setContacts(contactsData);
        setQuotes(quotesData);
      } else {
        toast.error('Session expirée');
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Déconnexion réussie');
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-900">
              <span className="text-orange-500">Orion Industrie</span> Admin
            </h1>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-gray-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Gallery Button */}
        <div className="mb-6 flex gap-4">
          <Button
            onClick={() => navigate('/admin/gallery')}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Image className="w-4 h-4 mr-2" />
            Gérer la Galerie Photos
          </Button>
          <Button
            onClick={() => navigate('/admin/realisations')}
            className="bg-[#020d2a] hover:bg-[#03152f] text-white"
          >
            <Package className="w-4 h-4 mr-2" />
            Gérer les Réalisations
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold mb-1">DEMANDES DE CONTACT</p>
                <p className="text-4xl font-bold text-slate-900">{contacts.length}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-4">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold mb-1">DEMANDES DE DEVIS</p>
                <p className="text-4xl font-bold text-slate-900">{quotes.length}</p>
              </div>
              <div className="bg-orange-100 rounded-full p-4">
                <Package className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('contacts')}
                className={`px-6 py-4 font-semibold transition-colors ${
                  activeTab === 'contacts'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Demandes de Contact ({contacts.length})
              </button>
              <button
                onClick={() => setActiveTab('quotes')}
                className={`px-6 py-4 font-semibold transition-colors ${
                  activeTab === 'quotes'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Demandes de Devis ({quotes.length})
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'contacts' && (
              <div className="space-y-4">
                {contacts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Aucune demande de contact pour le moment</p>
                ) : (
                  contacts.map((contact) => (
                    <div key={contact.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="bg-blue-100 rounded-full p-2">
                            <Mail className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-900 text-lg">{contact.name}</h3>
                            <p className="text-sm text-gray-500 flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(contact.created_at)}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          contact.status === 'new' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {contact.status === 'new' ? 'Nouveau' : contact.status}
                        </span>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center space-x-2 text-gray-700">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <a href={`mailto:${contact.email}`} className="hover:text-orange-600">
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-700">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <a href={`tel:${contact.phone}`} className="hover:text-orange-600">
                            {contact.phone}
                          </a>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-md p-4 border border-gray-200">
                        <div className="flex items-start space-x-2">
                          <MessageSquare className="w-4 h-4 text-gray-400 mt-1" />
                          <p className="text-gray-700 text-sm">{contact.message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'quotes' && (
              <div className="space-y-4">
                {quotes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Aucune demande de devis pour le moment</p>
                ) : (
                  quotes.map((quote) => (
                    <div key={quote.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="bg-orange-100 rounded-full p-2">
                            <Package className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-900 text-lg">{quote.name}</h3>
                            <p className="text-sm text-gray-600 flex items-center">
                              <Building className="w-4 h-4 mr-1" />
                              {quote.company}
                            </p>
                            <p className="text-sm text-gray-500 flex items-center mt-1">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(quote.created_at)}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          quote.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {quote.status === 'pending' ? 'En attente' : quote.status}
                        </span>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center space-x-2 text-gray-700">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <a href={`mailto:${quote.email}`} className="hover:text-orange-600">
                            {quote.email}
                          </a>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-700">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <a href={`tel:${quote.phone}`} className="hover:text-orange-600">
                            {quote.phone}
                          </a>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-md p-4 border border-gray-200 space-y-2">
                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 font-semibold">Type d'équipement:</span>
                            <p className="text-gray-900">{quote.equipment_type}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 font-semibold">Hauteur de quai:</span>
                            <p className="text-gray-900">{quote.dock_height}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 font-semibold">Quantité:</span>
                            <p className="text-gray-900">{quote.quantity}</p>
                          </div>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500 font-semibold">Lieu d'installation:</span>
                          <p className="text-gray-900">{quote.address}</p>
                        </div>
                        {quote.details && (
                          <div className="text-sm pt-2 border-t border-gray-200">
                            <span className="text-gray-500 font-semibold">Détails:</span>
                            <p className="text-gray-700 mt-1">{quote.details}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
